import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { z } from 'zod';
import Tabs from './Tabs';
import { FaChevronRight, FaChevronDown, FaFolder, FaList } from 'react-icons/fa';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

// Zod schema for validation
const permissionSchema = z.object({
  permission_name: z
    .string()
    .min(3, 'Permission name must be at least 3 characters')
    .max(50, 'Permission name cannot exceed 50 characters'),
  description: z
    .string()
    .min(5, 'Description must be at least 5 characters')
    .max(200, 'Description cannot exceed 200 characters'),
  parent_id: z.number().optional().nullable(),
  permission_type: z.enum(['main', 'sub', 'page']).default('page'),
  route_path: z.string().optional().nullable(),
});

function ManagePermissions() {
  const [roles, setRoles] = useState([]);
  const [permissionsTree, setPermissionsTree] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [errors, setErrors] = useState({
    permission_name: '',
    description: '',
  });
  const [selectAll, setSelectAll] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('tree'); // "tree" or "list"

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }

        const userData = await response.json();
        if (userData && userData.email) {
          setEmail(userData.email);
          fetchRoles(userData.email);
          fetchPermissions(userData.email);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        toast.error('Failed to load user information');
      }
    };

    fetchCurrentUser();
  }, []);

  const fetchRoles = async (userEmail) => {
    try {
      const response = await fetch(`${AQ_BASE_URL}/aq/api/roles/getAllRoles?email=${userEmail}`);
      const data = await response.json();
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Error fetching roles');
    }
  };

  const fetchPermissions = async (userEmail) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${AQ_BASE_URL}/aq/api/roles/getAllPermissions?email=${userEmail}`
      );
      const data = await response.json();

      if (data.success) {
        const { main_modules = [], sub_modules = [], pages = [] } = data.data || {};

        // Filter main modules to only show the 6 you specified
        const filteredMainModules = main_modules.filter((module) =>
          [
            'Dashboard',
            'Master Data Management',
            'Core Modules',
            'Reports & Analytics',
            'Help',
            'System Configurations',
          ].includes(module.permission_name)
        );

        const tree = buildPermissionTree(filteredMainModules, sub_modules, pages);
        setPermissionsTree(tree);

        // Auto-expand main modules
        const expanded = new Set();
        tree.forEach((node) => expanded.add(node.id));
        setExpandedNodes(expanded);
      } else {
        toast.error('Failed to load permissions');
        console.error('API Error:', data);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Error fetching permissions');
    } finally {
      setLoading(false);
    }
  };

  const buildPermissionTree = (mainModules, subModules, pages) => {
    const tree = [];

    // Sort main modules in specific order
    const mainOrder = [
      'Dashboard',
      'Master Data Management',
      'Core Modules',
      'Reports & Analytics',
      'Help',
      'System Configurations',
    ];
    const sortedMainModules = [...mainModules].sort(
      (a, b) => mainOrder.indexOf(a.permission_name) - mainOrder.indexOf(b.permission_name)
    );

    // Create map for quick lookup
    const allItems = [...sortedMainModules, ...subModules, ...pages];
    const itemMap = new Map();

    allItems.forEach((item) => {
      itemMap.set(item.id, {
        ...item,
        children: [],
        level: 0,
        isExpanded: true,
      });
    });

    // Build hierarchy
    sortedMainModules.forEach((main) => {
      const mainNode = itemMap.get(main.id);
      mainNode.level = 0;
      tree.push(mainNode);

      // Add sub-modules under main
      subModules
        .filter((sub) => sub.parent_id === main.id)
        .forEach((sub) => {
          const subNode = itemMap.get(sub.id);
          subNode.level = 1;
          mainNode.children.push(subNode);

          // Add pages under sub
          pages
            .filter((page) => page.parent_id === sub.id)
            .forEach((page) => {
              const pageNode = itemMap.get(page.id);
              pageNode.level = 2;
              subNode.children.push(pageNode);
            });
        });

      // Add pages directly under main (no sub-module)
      pages
        .filter((page) => page.parent_id === main.id)
        .forEach((page) => {
          const pageNode = itemMap.get(page.id);
          pageNode.level = 1;
          mainNode.children.push(pageNode);
        });
    });

    return tree;
  };

  const fetchRolePermissions = async (roleId) => {
    if (!email) return;

    try {
      const response = await fetch(
        `${AQ_BASE_URL}/aq/api/roles/${roleId}/getRolePermissions?email=${email}`
      );
      const data = await response.json();

      const rolePermissions = data.map((permission) => permission.permission_id);

      setSelectedPermissions(rolePermissions);
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      toast.error('Error fetching role permissions');
    }
  };

  const handlePermissionChange = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleRoleChange = (e) => {
    const selectedRoleId = e.target.value;
    setSelectedRole(selectedRoleId);
    fetchRolePermissions(selectedRoleId);
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedPermissions([]);
    } else {
      const allIds = getAllPermissionIds(permissionsTree);
      setSelectedPermissions(allIds);
    }
    setSelectAll(!selectAll);
  };

  const getAllPermissionIds = (nodes) => {
    let ids = [];
    nodes.forEach((node) => {
      ids.push(node.id);
      if (node.children && node.children.length > 0) {
        ids = [...ids, ...getAllPermissionIds(node.children)];
      }
    });
    return ids;
  };

  const handleAssignPermissions = async () => {
    if (!selectedRole) {
      toast.error('Please select a role before assigning permissions.');
      return;
    }

    if (!email) {
      toast.error('User email not available');
      return;
    }

    if (selectedPermissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    const uniquePermissions = [...new Set(selectedPermissions)];

    const requestBody = {
      permission_ids: uniquePermissions,
    };

    try {
      const response = await fetch(
        `${AQ_BASE_URL}/aq/api/roles/${selectedRole}/AssignPermissions?email=${email}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Permissions assigned successfully!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to assign permissions');
      }
    } catch (error) {
      console.error('Error assigning permissions:', error);
      toast.error('Error assigning permissions');
    }
  };

  const renderTree = (nodes) => {
    return nodes.map((node) => {
      const isExpanded = expandedNodes.has(node.id);
      const hasChildren = node.children && node.children.length > 0;
      const marginLeft = node.level * 20;

      return (
        <div key={node.id}>
          {/* Node */}
          <div
            className="flex items-center py-1 hover:bg-gray-50 rounded"
            style={{ marginLeft: `${marginLeft}px` }}
          >
            {/* Expand/Collapse Button */}
            {hasChildren && (
              <button
                onClick={() => toggleNode(node.id)}
                className="mr-1 p-1 hover:bg-gray-200 rounded"
              >
                {isExpanded ? (
                  <FaChevronDown className="w-3 h-3 text-gray-800" />
                ) : (
                  <FaChevronRight className="w-3 h-3 text-gray-800" />
                )}
              </button>
            )}

            {!hasChildren && <div className="w-6" />}

            {/* Checkbox */}
            <input
              type="checkbox"
              id={`perm-${node.id}`}
              checked={selectedPermissions.includes(node.id)}
              onChange={() => handlePermissionChange(node.id)}
              className="mr-3 h-4 w-4 rounded border-gray-300 text-gray-800 focus:ring-gray-800"
            />

            {/* Permission Info */}
            <label htmlFor={`perm-${node.id}`} className="flex-1 cursor-pointer">
              <div className="flex items-center">
                <span
                  className={`font-medium text-gray-800 ${
                    node.permission_type === 'main'
                      ? 'text-base'
                      : node.permission_type === 'sub'
                        ? 'text-sm'
                        : 'text-sm'
                  }`}
                >
                  {node.permission_name}
                </span>
              </div>
            </label>
          </div>

          {/* Children */}
          {hasChildren && isExpanded && (
            <div className="ml-4 border-l border-gray-200">{renderTree(node.children)}</div>
          )}
        </div>
      );
    });
  };

  const renderListView = () => {
    return permissionsTree
      .flatMap((main) => [
        main,
        ...main.children.flatMap((child) => [child, ...(child.children || [])]),
      ])
      .map((node) => (
        <div key={node.id} className="border-b border-gray-100 py-2">
          <div className="flex items-start">
            <input
              type="checkbox"
              id={`list-perm-${node.id}`}
              checked={selectedPermissions.includes(node.id)}
              onChange={() => handlePermissionChange(node.id)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-gray-800 focus:ring-gray-800"
            />
            <label htmlFor={`list-perm-${node.id}`} className="ml-3 flex-1 cursor-pointer">
              <div className="flex items-center">
                <span
                  className={`font-medium text-gray-800 ${
                    node.permission_type === 'main'
                      ? 'text-base'
                      : node.permission_type === 'sub'
                        ? 'text-sm'
                        : 'text-sm'
                  }`}
                >
                  {node.permission_name}
                </span>
              </div>
            </label>
          </div>
        </div>
      ));
  };

  return (
    <div className="p-4">
      <Tabs />
      <ToastContainer position="top-right" className="mt-10" />

      {/* Main Container */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Manage Permissions</h1>
              <p className="text-sm text-gray-600 mt-1">Assign permissions to roles</p>
            </div>

            <div className="flex items-center gap-4">
              {/* View Toggle */}
              <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setViewMode('tree')}
                  className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-sm ${
                    viewMode === 'tree'
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-700 hover:text-gray-800'
                  }`}
                >
                  <FaFolder className="w-3.5 h-3.5" />
                  Tree
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-sm ${
                    viewMode === 'list'
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-700 hover:text-gray-800'
                  }`}
                >
                  <FaList className="w-3.5 h-3.5" />
                  List
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Role Selection Section */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Role</label>
            <div className="flex items-center gap-4">
              <select
                value={selectedRole}
                onChange={handleRoleChange}
                className="flex-1 border border-gray-300 rounded-lg py-2.5 px-4 bg-white focus:ring-2 focus:ring-gray-800 focus:border-gray-800 text-gray-800"
                disabled={loading}
              >
                <option value="" disabled>
                  Choose a role...
                </option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.role_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading permissions...</p>
            </div>
          ) : (
            <>
              {/* Permissions Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800">Permissions</h2>
                    <p className="text-sm text-gray-600">
                      {viewMode === 'tree'
                        ? 'Hierarchical view showing parent-child relationships'
                        : 'Simple list view of all permissions'}
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{selectedPermissions.length}</span> selected
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                      />
                      <span className="text-sm font-medium text-gray-800">Select All</span>
                    </label>
                  </div>
                </div>

                {/* View Mode Content */}
                {permissionsTree.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 border border-gray-200 rounded-lg">
                    <FaFolder className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-lg font-medium">No permissions found</p>
                    <p className="text-sm">Contact admin to set up permissions</p>
                  </div>
                ) : (
                  <div
                    className={`
                    ${viewMode === 'tree' ? 'border border-gray-200 rounded-lg p-4' : ''}
                  `}
                  >
                    {viewMode === 'tree' ? renderTree(permissionsTree) : renderListView()}
                  </div>
                )}

                {/* Main Modules Line Display */}
                <div className="mt-8">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Main Modules Available</h3>
                  <div className="flex flex-wrap gap-2">
                    {permissionsTree
                      .filter((node) => node.permission_type === 'main')
                      .map((main) => (
                        <div
                          key={main.id}
                          className="inline-flex items-center px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-md"
                        >
                          <span className="text-sm font-medium text-gray-800">
                            {main.permission_name}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* Action Section */}
              {selectedPermissions.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        <span className="font-medium text-gray-800">
                          {selectedPermissions.length} permission(s)
                        </span>{' '}
                        selected for assignment
                      </p>
                      {selectedRole && (
                        <p className="text-sm text-gray-600 mt-1">
                          Will be assigned to:{' '}
                          <span className="font-medium">
                            {roles.find((r) => r.id === parseInt(selectedRole))?.role_name}
                          </span>
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handleAssignPermissions}
                      className="px-6 py-2.5 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!selectedRole}
                    >
                      {selectedRole ? 'Assign Permissions' : 'Select a Role First'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManagePermissions;

// import React, { useState, useEffect } from "react";
// import { toast, ToastContainer } from "react-toastify";
// import { z } from "zod";
// import Tabs from "./Tabs";

// const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
// // Zod schema for validation
// const permissionSchema = z.object({
//   permission_name: z
//     .string()
//     .min(3, "Permission name must be at least 3 characters")
//     .max(50, "Permission name cannot exceed 50 characters"),
//   description: z
//     .string()
//     .min(5, "Description must be at least 5 characters")
//     .max(200, "Description cannot exceed 200 characters"),
// });

// function ManagePermissions() {
//   const [roles, setRoles] = useState([]);
//   const [permissions, setPermissions] = useState([]);
//   const [selectedRole, setSelectedRole] = useState("");
//   const [selectedPermissions, setSelectedPermissions] = useState([]);
//   const [isPopupOpen, setIsPopupOpen] = useState(false);
//   const [newPermissionName, setNewPermissionName] = useState("");
//   const [newPermissionDescription, setNewPermissionDescription] = useState("");
//   const [errors, setErrors] = useState({
//     permission_name: "",
//     description: "",
//   });
//   const [selectAll, setSelectAll] = useState(false);
//   const [email, setEmail] = useState("");

//   useEffect(() => {
//     // Fetch current user's email when component mounts
//     const fetchCurrentUser = async () => {
//       try {
//         const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
//           credentials: 'include'
//         });

//         if (!response.ok) {
//           throw new Error("Failed to fetch user data");
//         }

//         const userData = await response.json();
//         if (userData && userData.email) {
//           setEmail(userData.email);
//           // Now that we have the email, fetch roles and permissions
//           fetchRoles(userData.email);
//           fetchPermissions(userData.email);
//         }
//       } catch (error) {
//         console.error("Failed to fetch user data:", error);
//         toast.error("Failed to load user information");
//       }
//     };

//     fetchCurrentUser();
//   }, []);

//   const fetchRoles = async (userEmail) => {
//     try {
//       const response = await fetch(
//         `${AQ_BASE_URL}/aq/api/roles/getAllRoles?email=${userEmail}`
//       );
//       const data = await response.json();
//       setRoles(data);
//     } catch (error) {
//       console.error("Error fetching roles:", error);
//     }
//   };

//   const fetchPermissions = async (userEmail) => {
//     try {
//       const response = await fetch(
//         `${AQ_BASE_URL}/aq/api/roles/getAllPermissions?email=${userEmail}`
//       );
//       const data = await response.json();
//       setPermissions(data);
//     } catch (error) {
//       console.error("Error fetching permissions:", error);
//     }
//   };

//   const fetchRolePermissions = async (roleId) => {
//     if (!email) return;

//     try {
//       const response = await fetch(
//         `${AQ_BASE_URL}/aq/api/roles/${roleId}/getRolePermissions?email=${email}`
//       );
//       const data = await response.json();

//       // Extract permission IDs from the response
//       const rolePermissions = data.map(
//         (permission) => permission.permission_id
//       );

//       setSelectedPermissions(rolePermissions); // Update selected permissions
//     } catch (error) {
//       console.error("Error fetching role permissions:", error);
//     }
//   };

//   const handlePermissionChange = (permissionId) => {
//     setSelectedPermissions((prev) =>
//       prev.includes(permissionId)
//         ? prev.filter((id) => id !== permissionId)
//         : [...prev, permissionId]
//     );
//   };

//   const handleRoleChange = (e) => {
//     const selectedRoleId = e.target.value;
//     setSelectedRole(selectedRoleId);
//     fetchRolePermissions(selectedRoleId); // Fetch permissions for the selected role
//   };

//   const handleAssignPermissions = async () => {
//     if (!selectedRole) {
//       alert("Please select a role before assigning permissions.");
//       return;
//     }

//     if (!email) {
//       toast.error("User email not available");
//       return;
//     }

//     // Ensure unique permission IDs (no duplicates)
//     const uniquePermissions = [...new Set(selectedPermissions)];

//     // Prepare the request body
//     const requestBody = {
//       permission_ids: uniquePermissions, // unique permission IDs to assign
//     };

//     try {
//       const response = await fetch(
//         `${AQ_BASE_URL}/aq/api/roles/${selectedRole}/AssignPermissions?email=${email}`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(requestBody), // Send the unique permissions
//         }
//       );

//       if (response.ok) {
//         const result = await response.json();
//         toast.success(result.message || "Permissions assigned successfully!");
//       } else {
//         toast.error("Failed to assign permissions");
//       }
//     } catch (error) {
//       console.error("Error assigning permissions:", error);
//       toast.error("Error assigning permissions");
//     }
//   };

//   const handleCreatePermission = async () => {
//     if (!email) {
//       toast.error("User email not available");
//       return;
//     }

//     try {
//       // Validate inputs using Zod
//       const result = permissionSchema.safeParse({
//         permission_name: newPermissionName,
//         description: newPermissionDescription,
//       });

//       if (!result.success) {
//         // Set error messages if validation fails
//         const newErrors = result.error.formErrors.fieldErrors;
//         setErrors({
//           permission_name: newErrors.permission_name
//             ? newErrors.permission_name[0]
//             : "",
//           description: newErrors.description ? newErrors.description[0] : "",
//         });
//         return;
//       }

//       const response = await fetch(
//         `${AQ_BASE_URL}/aq/api/roles/createPermission?email=${email}`,
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify({
//             permission_name: newPermissionName,
//             description: newPermissionDescription,
//           }),
//         }
//       );

//       if (response.ok) {
//         toast.success("Permission created successfully!");
//         fetchPermissions(email); // Refresh the permissions list.
//         setIsPopupOpen(false);
//         setNewPermissionName("");
//         setNewPermissionDescription("");
//         setErrors({ permission_name: "", description: "" });
//       } else {
//         toast.error("Failed to create permission");
//       }
//     } catch (error) {
//       console.error("Error creating permission:", error);
//       toast.error("Error creating permission");
//     }
//   };

//   const handleSelectAllPermissions = () => {
//     if (selectAll) {
//       setSelectedPermissions([]);
//     } else {
//       setSelectedPermissions(permissions.map((permission) => permission.id));
//     }
//     setSelectAll(!selectAll);
//   };

//   return (
//     <div className="p-4">
//       <Tabs />
//       <ToastContainer position="top-right" className="mt-10" />
//       <h1 className="text-xl font-bold mb-4 text-black">Manage Permissions</h1>

//       {/* Role Selection and Create Permission */}
//       <div className="flex items-center gap-4 mb-4 w-full">
//         <select
//           value={selectedRole}
//           onChange={handleRoleChange}
//           className="border rounded-lg py-2 px-4 w-2/3"
//         >
//           <option value="" disabled>Select Role</option>
//           {roles.map((role) => (
//             <option key={role.id} value={role.id}>
//               {role.role_name}
//             </option>
//           ))}
//         </select>

//       </div>

//       {/* Permissions List */}
//       <div className="mb-4">
//         <div className="flex justify-start gap-36">
//         <h2 className="text-lg font-bold mb-2">Permissions</h2>
//         <label className="block mb-2">
//           <input
//             type="checkbox"
//             checked={selectAll}
//             onChange={handleSelectAllPermissions}
//             className="mr-2 h-4 w-4"
//           />
//           Select All
//         </label>
//         </div>
//         {permissions.length > 0 ? (
//           permissions.map((permission) => (
//             <label key={permission.id} className="block mb-2">
//               <input
//                 type="checkbox"
//                 value={permission.id}
//                 checked={selectedPermissions.includes(permission.id)}
//                 onChange={() => handlePermissionChange(permission.id)}
//                 className="mr-2 h-4 w-4 "
//               />
//               {permission.permission_name}
//             </label>
//           ))
//         ) : (
//           <p>No permissions available.</p>
//         )}
//       </div>

//       {/* Assign Permissions Button */}
//       <button
//         onClick={handleAssignPermissions}
//         className="bg-black text-white hover:text-white px-6 py-2 rounded-lg"
//         disabled={!selectedRole || selectedPermissions.length === 0}
//       >
//         Assign Permissions
//       </button>

//       {/* Popup for Creating Permission */}
//       {isPopupOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
//           <div className="bg-white p-6 rounded-lg shadow-lg w-96">
//             <h2 className="text-lg font-bold mb-4">Create Permission</h2>
//             <div className="mb-4">
//               <label className="block mb-2 font-semibold">
//                 Permission Name
//               </label>
//               <input
//                 type="text"
//                 value={newPermissionName}
//                 onChange={(e) => setNewPermissionName(e.target.value)}
//                 className="w-full border rounded-lg py-2 px-3"
//               />
//               {errors.permission_name && (
//                 <p className="text-red-500 text-sm mt-1">
//                   {errors.permission_name}
//                 </p>
//               )}
//             </div>
//             <div className="mb-4">
//               <label className="block mb-2 font-semibold">Description</label>
//               <textarea
//                 value={newPermissionDescription}
//                 onChange={(e) => setNewPermissionDescription(e.target.value)}
//                 className="w-full border rounded-lg py-2 px-3"
//                 rows="4"
//               />
//               {errors.description && (
//                 <p className="text-red-500 text-sm mt-1">
//                   {errors.description}
//                 </p>
//               )}
//             </div>
//             <div className="flex justify-between">
//               <button
//                 onClick={() => setIsPopupOpen(false)}
//                 className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleCreatePermission}
//                 className="bg-blue-500 text-white px-4 py-2 rounded-lg"
//               >
//                 Create
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default ManagePermissions;
